import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaFileExcel, FaUpload, FaSpinner, FaCheck, FaDownload, FaHistory } from 'react-icons/fa';
import boletasPlanillaApi from '../../services/api/boletasPlanillaApi';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';

const MESES = [
  { v: 1, l: 'Enero' }, { v: 2, l: 'Febrero' }, { v: 3, l: 'Marzo' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Mayo' }, { v: 6, l: 'Junio' },
  { v: 7, l: 'Julio' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Septiembre' },
  { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
];

const CargaBoletasPlanilla = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();
  const fileRef = useRef(null);

  const [periodoMes, setPeriodoMes] = useState(new Date().getMonth() + 1);
  const [periodoAnio, setPeriodoAnio] = useState(new Date().getFullYear());
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);

  const loadLotes = useCallback(async () => {
    if (!proyectoId) {
      setLotes([]);
      return;
    }

    setLoadingLotes(true);
    try {
      const res = await boletasPlanillaApi.getLotes(proyectoId);
      setLotes(res.data?.data || []);
    } catch {
      setLotes([]);
    } finally {
      setLoadingLotes(false);
    }
  }, [proyectoId]);

  useEffect(() => {
    loadLotes();
  }, [loadLotes]);

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('proyecto_id', proyectoId);
    fd.append('periodo_mes', periodoMes);
    fd.append('periodo_anio', periodoAnio);
    fd.append('file', file);
    return fd;
  };

  const handleDescargarModelo = async () => {
    setDownloading(true);
    try {
      await boletasPlanillaApi.downloadModeloFormatoFijo();
    } catch {
      setMsg({ type: 'error', text: 'No se pudo descargar el modelo Excel' });
    } finally {
      setDownloading(false);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      setMsg({ type: 'error', text: 'Seleccione el archivo Excel de planilla' });
      return;
    }
    setValidating(true);
    setPreview(null);
    try {
      const res = await boletasPlanillaApi.validateCarga(buildFormData());
      setPreview(res.data?.data);
      setMsg({
        type: 'success',
        text: `Validación: ${res.data?.data?.resumen?.ok} OK / ${res.data?.data?.resumen?.error} con error`,
      });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Error al validar' });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    if (!file) return;
    if (!preview || preview.resumen?.ok === 0) {
      setMsg({ type: 'error', text: 'Valide primero y asegúrese de tener registros OK' });
      return;
    }
    setSaving(true);
    try {
      const fd = buildFormData();
      const res = await boletasPlanillaApi.guardarCarga(fd);
      setMsg({ type: 'success', text: `Carga guardada. Lote #${res.data?.data?.id}` });
      await loadLotes();
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Error al guardar carga' });
    } finally {
      setSaving(false);
    }
  };

  if (needsProyectoSelection) {
    return (
      <div className="dh-reporte-bg p-4">
        <RrhhProyectoSelector
          isCorporativo={isCorporativo}
          proyectos={proyectos}
          proyectoId={proyectoId}
          onChange={setProyectoId}
          loading={loadingProyectos}
        />
        <div className="alert alert-info">Seleccione un proyecto.</div>
      </div>
    );
  }

  return (
    <div className="dh-reporte-bg" style={{ minHeight: '100vh', padding: '1.5rem' }}>
      <style>{`
        .dh-reporte-bg { background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%); }
        .dh-main-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #fff; padding: 1.5rem; border-radius: 12px; margin-bottom: 1.5rem; }
        .dh-card { background: #fff; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); padding: 1.25rem; margin-bottom: 1rem; }
        .btn-dh-primary { background: linear-gradient(135deg, #11998e, #38ef7d); color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; font-weight: 600; }
        .btn-dh-secondary { background: #6c757d; color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 8px; }
        .dh-form-label { font-weight: 600; font-size: 0.85rem; }
        .dh-form-control { border-radius: 8px; border: 1px solid #ddd; padding: 0.45rem 0.65rem; width: 100%; }
        .dh-alert-info { background: #e8f4fd; border: 1px solid #b6d4fe; border-radius: 8px; padding: 0.75rem 1rem; color: #084298; }
      `}</style>

      <RrhhProyectoSelector
        isCorporativo={isCorporativo}
        proyectos={proyectos}
        proyectoId={proyectoId}
        onChange={setProyectoId}
        loading={loadingProyectos}
      />

      {msg && (
        <div className={`alert ${msg.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
          {msg.text}
          <button type="button" className="btn-close float-end" onClick={() => setMsg(null)} />
        </div>
      )}

      <div className="dh-main-header">
        <h1 className="mb-1"><FaFileExcel className="me-2" />Carga Planilla Boletas</h1>
        <p className="mb-0 opacity-75">Formato fijo TAREO — sin homologación de columnas</p>
      </div>

      <div className="dh-card">
        <div className="dh-alert-info mb-3">
          Use el Excel con encabezados en la fila 1 (ITEM, DNI, APELLIDOS Y NOMBRES, CARGO, SUELDO, etc.).
          El nombre del trabajador se imprime junto en la boleta PDF.
        </div>
        <div className="dh-alert-info mb-3">
          Los datos de empresa ya se obtienen automáticamente desde la tabla `empresas`.
        </div>

        <div className="row g-3">
          <div className="col-md-2">
            <label className="dh-form-label">Mes</label>
            <select className="dh-form-control" value={periodoMes} onChange={(e) => setPeriodoMes(Number(e.target.value))}>
              {MESES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="dh-form-label">Año</label>
            <input type="number" className="dh-form-control" value={periodoAnio}
              onChange={(e) => setPeriodoAnio(Number(e.target.value))} />
          </div>
          <div className="col-md-8">
            <label className="dh-form-label">Archivo Excel planilla</label>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="dh-form-control"
              onChange={(e) => setFile(e.target.files[0] || null)} />
          </div>
        </div>

        <div className="mt-3 d-flex gap-2 flex-wrap">
          <button type="button" className="btn-dh-secondary" onClick={handleDescargarModelo} disabled={downloading}>
            {downloading ? <FaSpinner className="fa-spin" /> : <FaDownload />} Descargar modelo
          </button>
          <button type="button" className="btn-dh-secondary" onClick={handleValidate} disabled={validating || !file}>
            {validating ? <FaSpinner className="fa-spin" /> : <FaUpload />} Validar
          </button>
          <button type="button" className="btn-dh-primary" onClick={handleSave} disabled={saving || !preview}>
            {saving ? <FaSpinner className="fa-spin" /> : <FaCheck />} Guardar carga
          </button>
        </div>
      </div>

      <div className="dh-card">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 className="mb-0"><FaHistory className="me-2" />Historial de cargas</h6>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn-dh-secondary"
              onClick={loadLotes}
              disabled={loadingLotes}
            >
              {loadingLotes ? <FaSpinner className="fa-spin" /> : 'Actualizar'}
            </button>
            <Link to="/rrhh/generacion-boletas" className="btn-dh-primary text-decoration-none">
              Ir a Generación
            </Link>
          </div>
        </div>

        {loadingLotes ? (
          <div className="text-muted"><FaSpinner className="fa-spin me-2" />Cargando historial...</div>
        ) : lotes.length === 0 ? (
          <div className="text-muted">Aún no hay cargas registradas para este proyecto.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Periodo</th>
                  <th>Archivo</th>
                  <th>OK</th>
                  <th>Errores</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => (
                  <tr key={lote.id}>
                    <td>#{lote.id}</td>
                    <td>{MESES.find((m) => m.v === lote.periodo_mes)?.l} {lote.periodo_anio}</td>
                    <td>{lote.archivo_nombre || '—'}</td>
                    <td>{lote.registros_ok}</td>
                    <td>{lote.registros_error}</td>
                    <td>{lote.estado}</td>
                    <td>{lote.created_at ? String(lote.created_at).slice(0, 16).replace('T', ' ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && (
        <div className="dh-card">
          <h6>Resumen validación ({preview.resumen?.formato})</h6>
          <p>Total: {preview.resumen?.total} | OK: {preview.resumen?.ok} | Errores: {preview.resumen?.error}</p>
          <div className="table-responsive" style={{ maxHeight: 400 }}>
            <table className="table table-sm table-bordered">
              <thead><tr><th>Fila</th><th>DNI</th><th>Trabajador</th><th>Estado</th><th>Detalle</th></tr></thead>
              <tbody>
                {(preview.registros || []).map((r) => (
                  <tr key={r.fila_excel} className={r.estado === 'error' ? 'table-danger' : ''}>
                    <td>{r.fila_excel}</td>
                    <td>{r.dni}</td>
                    <td>{r.nombres_completos}</td>
                    <td>{r.estado}</td>
                    <td className="small">{r.error_mensaje || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargaBoletasPlanilla;
